// Single source of truth for word audio — built from CDN vocab_dictionary listing.
// CDN audio: https://videos.littlelambenglish.com/vocab_dictionary/{word}.mp3
import cdnVocabWords from './cdnVocabWords.json' with { type: 'json' };
import { getTranslation } from './translations.js';
import { parseNumericToken, numberToEnglishWords, numberToChinese } from './numberWords.js';
import { textToVocabFilename } from './itemsHelper.js';

const CDN_BASE = 'https://videos.littlelambenglish.com/vocab_dictionary';

/** Get the CDN audio URL for a word. */
export function vocabAudioUrl(word) {
  return `${CDN_BASE}/${textToVocabFilename(word)}.mp3`;
}

/** A whole sentence's recording — its own folder, so the word list (cdnVocabWords) stays words only. */
export function sentenceAudioUrl(text) {
  // encoded: a sentence can hold a ? ("Where are you? I am here."), which would start a query
  return `${CDN_BASE.replace(/vocab_dictionary$/, 'sentence_audio')}/${encodeURIComponent(textToVocabFilename(text))}.mp3`;
}

function createDictionary() {
  const result = {};
  for (const word of cdnVocabWords) {
    const lower = word.toLowerCase();
    if (result[lower]) continue;
    const translation = getTranslation(lower);
    result[lower] = {
      english: word,
      chinese: translation.chinese,
      pinyin: translation.pinyin,
      audioUrl: vocabAudioUrl(word),
    };
  }
  return result;
}

export const dictionary = createDictionary();

// Synthesised number entries are built on demand (digits are infinite, so they
// can't live in `dictionary`) and cached — a page of text repeats the same numbers.
const numberEntryCache = new Map();

/**
 * Build a dictionary-shaped entry for a plain number token by stitching together
 * the number-word recordings that already exist on the CDN. Returns null when the
 * token isn't a plain number.
 */
function getNumberEntry(key) {
  if (numberEntryCache.has(key)) return numberEntryCache.get(key);

  const value = parseNumericToken(key);
  const words = value === null ? [] : numberToEnglishWords(value);
  // Guard the contract rather than assume it: an entry whose audio doesn't exist
  // would render as tappable and then play nothing.
  const entry = words.length > 0 && words.every(hasVocabAudio)
    ? {
      english: key,
      chinese: numberToChinese(value),
      pinyin: null,
      audioUrl: vocabAudioUrl(words[0]),
      audioUrls: words.map(vocabAudioUrl),
      isNumber: true,
    }
    : null;

  numberEntryCache.set(key, entry);
  return entry;
}

export function getWord(wordKey) {
  if (!wordKey) return null;
  const key = wordKey.toLowerCase();
  // A real uploaded recording always wins — 27 digits ('3', '10', '1628'…) have a
  // human reading the number, which beats a stitched-together one.
  return dictionary[key] || getNumberEntry(key);
}

/**
 * The dictionary word a plain number is SAID as, when that word has its own
 * recording: "46" → "forty-six", "7" → "seven". Null for anything else (not a
 * number, or no single recording — "42" until forty-two is recorded, "1084").
 *
 * For callers that turn a picked word into a practice item: the item's audio is
 * `vocab_dictionary/<word>.mp3`, and "46.mp3" doesn't exist — "forty-six.mp3" does.
 */
export function spokenNumberKey(token) {
  const value = parseNumericToken(token);
  if (value === null || value < 1 || value > 100) return null;
  const words = numberToEnglishWords(value);
  const key = value === 100 ? 'one_hundred' : words.join('-');
  return dictionary[key] ? key : null;
}

/**
 * Every audio URL for an entry, in playback order. Number entries have one per
 * spoken word ("21" → twenty, one); everything else has exactly one.
 */
export function wordAudioUrls(entry) {
  if (!entry) return [];
  return entry.audioUrls || (entry.audioUrl ? [entry.audioUrl] : []);
}

/**
 * Whether a word has a real, uploaded CDN audio file — as opposed to
 * `vocabAudioUrl`, which always constructs a URL regardless of whether
 * anything actually exists at it. Callers that need to know "will this
 * actually play" (e.g. gating a listening-only exercise) should check this
 * before relying on vocabAudioUrl's output.
 */
export function hasVocabAudio(word) {
  return !!dictionary[word.toLowerCase()];
}

/**
 * Suffix → list of transforms that turn an inflected form into candidate roots.
 * Each transform receives the word with the suffix still attached and returns
 * a candidate base form. The suffix is only tried when `w.length > suffix.length`.
 *
 * `doubled` means: after stripping the suffix, the new last char is doubled
 * (e.g. 'stopp' from 'stopped'), which signals a consonant was doubled before -ed/-ing.
 */
const INFLECTION_RULES = [
  // suffix, [ (w) => candidate, ... ]
  ["'s",  [(w) => w.slice(0, -2)]],
  ['ies', [(w) => w.slice(0, -3) + 'y']],                       // babies → baby
  ['ied', [(w) => w.slice(0, -3) + 'y']],                       // studied → study
  ['ing', [
    (w) => w.slice(0, -3),                                      // walking → walk
    (w) => w.slice(0, -3) + 'e',                                // making → make
    (w) => (isDoubled(w, 3) ? w.slice(0, -4) : null),           // running → run
  ]],
  ['ed', [
    (w) => w.slice(0, -2),                                      // walked → walk
    (w) => w.slice(0, -1),                                      // liked → like
    (w) => (isDoubled(w, 2) ? w.slice(0, -3) : null),           // stopped → stop
  ]],
  ['est', [
    (w) => w.slice(0, -3),                                      // tallest → tall
    (w) => w.slice(0, -2),                                      // nicest → nice
  ]],
  ['er', [
    (w) => w.slice(0, -2),                                      // taller → tall
    (w) => w.slice(0, -1),                                      // biker → bike
  ]],
  ['es', [
    (w) => w.slice(0, -1),                                      // dances → dance
    (w) => w.slice(0, -2),                                      // boxes → box
  ]],
  ['s',  [(w) => w.slice(0, -1)]],
  ['ly', [(w) => w.slice(0, -2)]],
];

function isDoubled(w, stripN) {
  const base = w.slice(0, -stripN);
  return base.length >= 2 && base[base.length - 1] === base[base.length - 2];
}

const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const TEENS = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function spellUnder100(n) {
  if (n < 10) return ONES[n];
  if (n < 20) return TEENS[n - 10];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return o === 0 ? TENS[t] : `${TENS[t]}_${ONES[o]}`;
}

function spellUnder1000(n) {
  if (n < 100) return spellUnder100(n);
  const h = Math.floor(n / 100);
  const r = n % 100;
  return r === 0 ? `${ONES[h]}_hundred` : `${ONES[h]}_hundred_${spellUnder100(r)}`;
}

/**
 * Convert a numeric string (`"21"`, `"1000"`) to its English word form using the
 * CDN's filename convention (lowercase, underscores between words). Mirrors the
 * inflection lookup pattern: a transform from surface form to dictionary key.
 *
 * Returns a word string for integers in 0..1,000,000 inclusive, or null for
 * anything else (negative, decimal, non-numeric, out of range).
 */
export function numeralToWord(s) {
  if (typeof s !== 'string' || !/^\d+$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0 || n > 1_000_000) return null;
  if (n === 0) return 'zero';

  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1_000);
  const rest = n % 1_000;

  const parts = [];
  if (millions > 0) parts.push(`${ONES[millions]}_million`);
  if (thousands > 0) parts.push(`${spellUnder1000(thousands)}_thousand`);
  if (rest > 0) parts.push(spellUnder1000(rest));
  return parts.join('_');
}

/**
 * If `word` is in the dictionary, return it (lowercased). Otherwise try common
 * English inflections (`'s`, `-s`, `-es`, `-ies`, `-ed`, `-ied`, `-ing`,
 * `-er`, `-est`, `-ly`, plus doubled-consonant and dropped-`e` variants) and
 * return the first root form that IS in the dictionary. Returns null on miss.
 */
export function findRootForm(word) {
  if (!word || typeof word !== 'string') return null;
  const w = word.toLowerCase();
  if (dictionary[w]) return w;

  const numWord = numeralToWord(w);
  if (numWord && dictionary[numWord]) return numWord;

  for (const [suffix, transforms] of INFLECTION_RULES) {
    if (!w.endsWith(suffix) || w.length <= suffix.length) continue;
    for (const transform of transforms) {
      const candidate = transform(w);
      if (candidate && dictionary[candidate]) return candidate;
    }
  }
  return null;
}

/**
 * Look up `word` in the dictionary; on miss, retry with its inflection root.
 * Returns the full dictionary entry (english/chinese/pinyin/audioUrl) or null.
 * This is the function to use for underline-worthiness and click-to-play —
 * it ensures `asked` routes to `ask`'s audio and translation.
 */
export function getWordOrRoot(word) {
  if (!word) return null;
  const direct = getWord(word);
  if (direct) return direct;
  const root = findRootForm(word);
  return root ? getWord(root) : null;
}
